// src/pages/EmployeesPage.jsx

import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Pencil, Trash2 } from "lucide-react";

export default function EmployeesPage() {
  const { user, loading: authLoading } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);

  const [nickDrafts, setNickDrafts] = useState({});
  const [nickBranchByEmployee, setNickBranchByEmployee] = useState({});
  const [nickSavingId, setNickSavingId] = useState("");

  const [form, setForm] = useState({
    fullName: "",
    position: "farmaceut",
    uvazek: 1.0,
    denniFond: 8,
    dovolenaZbyva: 200,
    prescasyZbyva: 0,
    pharmacyCode: "",
    isFlying: false,
    allowedPharmacyCodes: [],
    email: "",
    phone: "",
  });

  const isUserOnly = user?.role === "user";
  const canManageEmployees = user?.role === "admin" || user?.role === "rm";

  const updateForm = (k, v) =>
    setForm((p) => {
      const next = { ...p };

      if (k === "uvazek") {
        const raw = String(v ?? "");
        const normalized = raw.replace(",", ".");

        if (!/^\d*\.?\d{0,2}$/.test(normalized)) return p;

        next.uvazek = normalized;

        const u = Number.parseFloat(normalized);
        if (Number.isFinite(u)) {
          next.denniFond = Math.round(u * 8 * 100) / 100;
        } else {
          next.denniFond = 0;
        }

        return next;
      }

      next[k] = v;
      return next;
    });

  const normalizeBranchCode = (value) =>
    String(value ?? "").trim().replace(/^0+(?=\d)/, "");

  const getNicknameForBranch = (emp, pharmacyCode) => {
    const target = normalizeBranchCode(pharmacyCode);
    if (!target) return "";

    const list = Array.isArray(emp.branchNicknames) ? emp.branchNicknames : [];
    const found = list.find(
      (x) => normalizeBranchCode(x.pharmacyCode) === target
    );

    return found?.nickname || "";
  };

  const getAvailableNicknameBranches = (emp) => {
    const own = normalizeBranchCode(user?.pharmacyCode);

    const baseBranches = emp.isFlying
      ? Array.isArray(emp.allowedPharmacyCodes)
        ? emp.allowedPharmacyCodes
        : []
      : [emp.pharmacyCode];

    const normalized = Array.from(
      new Set(baseBranches.map(normalizeBranchCode).filter(Boolean))
    );

    if (user?.role === "admin") {
      return normalized;
    }

    if (user?.role === "rm") {
      const regions = Array.from(
        new Set((user.regions || []).map(normalizeBranchCode).filter(Boolean))
      );
      return normalized.filter((code) => regions.includes(code));
    }

    if (user?.role === "user") {
      return normalized.includes(own) ? [own] : [];
    }

    return [];
  };

  const getSelectedNicknameBranch = (emp) => {
    const available = getAvailableNicknameBranches(emp);
    if (!available.length) return "";

    const selected = normalizeBranchCode(nickBranchByEmployee[emp._id]);
    if (selected && available.includes(selected)) return selected;

    return available[0];
  };

  const handleNicknameBranchChange = (emp, pharmacyCode) => {
    const normalized = normalizeBranchCode(pharmacyCode);

    setNickBranchByEmployee((prev) => ({
      ...prev,
      [emp._id]: normalized,
    }));

    setNickDrafts((prev) => ({
      ...prev,
      [emp._id]: getNicknameForBranch(emp, normalized),
    }));
  };

  const handleNicknameInputChange = (emp, value) => {
    setNickDrafts((prev) => ({
      ...prev,
      [emp._id]: value,
    }));
  };

  const handleSaveNickname = async (emp) => {
    const pharmacyCode = getSelectedNicknameBranch(emp);

    if (!pharmacyCode) {
      setError("Pro tohoto zaměstnance nelze nastavovat přezdívku na žádné pobočce.");
      return;
    }

    try {
      setNickSavingId(emp._id);
      setError("");

      const nickname = String(
        nickDrafts[emp._id] ?? getNicknameForBranch(emp, pharmacyCode) ?? ""
      ).trim();

      const res = await fetch(`/api/employees/${emp._id}/nickname`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pharmacyCode,
          nickname,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Nepodařilo se uložit přezdívku.");
      }

      setEmployees((prev) => prev.map((x) => (x._id === emp._id ? data : x)));

      setNickDrafts((prev) => ({
        ...prev,
        [emp._id]: getNicknameForBranch(data, pharmacyCode),
      }));
    } catch (err) {
      console.error("Nickname save error:", err);
      setError(err.message || "Nepodařilo se uložit přezdívku.");
    } finally {
      setNickSavingId("");
    }
  };

  const handleDeleteNickname = async (emp) => {
    const pharmacyCode = getSelectedNicknameBranch(emp);

    if (!pharmacyCode) {
      setError("Pro tohoto zaměstnance nelze smazat přezdívku na žádné pobočce.");
      return;
    }

    try {
      setNickSavingId(emp._id);
      setError("");

      const res = await fetch(`/api/employees/${emp._id}/nickname`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pharmacyCode,
          nickname: "",
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Nepodařilo se smazat přezdívku.");
      }

      setEmployees((prev) => prev.map((x) => (x._id === emp._id ? data : x)));

      setNickDrafts((prev) => ({
        ...prev,
        [emp._id]: "",
      }));
    } catch (err) {
      console.error("Nickname delete error:", err);
      setError(err.message || "Nepodařilo se smazat přezdívku.");
    } finally {
      setNickSavingId("");
    }
  };  
  // ---------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!user) return;

    if (user.role === "admin") {
      const loadBranches = async () => {
        try {
          const res = await fetch(`/api/users/${user.firma}/branches`, {
            credentials: "include",
          });

          if (!res.ok) return;

          const data = await res.json();
          setBranches(Array.isArray(data) ? data : []);
        } catch (e) {
          console.error("Branches error", e);
        }
      };

      loadBranches();
      return;
    }

    if (user.role === "rm") {
      setBranches(Array.isArray(user.regions) ? user.regions : []);
      return;
    }

    setBranches([]);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadEmployees = async () => {
      try {
        setLoading(true);
        setError("");

        let url = "";
        if (user.role === "admin" || user.role === "rm") {
          url = `/api/employees/${user.firma}`;
        } else {
          url = `/api/employees/${user.firma}/${user.pharmacyCode}/visible`;
        }

        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error("Nepodařilo se načíst zaměstnance.");

        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
      } catch (err) {
        console.error("Employees error:", err);
        setError(err.message || "Nepodařilo se načíst zaměstnance.");
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, [user]);

  // ---------------------------------------------------------------------
  // EARLY RETURNS
  // ---------------------------------------------------------------------

  if (authLoading) return <div className="p-6">Načítám uživatele…</div>;
  if (!user) return <div className="p-6 text-red-600">Nejste přihlášen.</div>;

  // ---------------------------------------------------------------------
  // FUNKCE
  // ---------------------------------------------------------------------

  const openCreate = () => {
    if (!canManageEmployees) {
      setError("Nemáte oprávnění přidávat zaměstnance.");
      return;
    }

    setEditEmployee(null);
    setError("");

    setForm({
      fullName: "",
      position: "farmaceut",
      uvazek: 1.0,
      denniFond: 8,
      dovolenaZbyva: 200,
      prescasyZbyva: 0,
      pharmacyCode: "",
      isFlying: false,
      allowedPharmacyCodes: [],
      email: "",
      phone: "",
    });

    setModalOpen(true);
  };

  const openEdit = (emp) => {
    if (!canManageEmployees) {
      setError("Nemáte oprávnění upravovat zaměstnance.");
      return;
    }

    setEditEmployee(emp);
    setError("");

    setForm({
      fullName: emp.fullName,
      position: emp.position,
      uvazek: Number.isFinite(Number(emp.uvazek))
        ? Number(emp.uvazek).toFixed(2)
        : "1.00",
      denniFond: emp.denniFond,
      dovolenaZbyva: emp.dovolenaZbyva,
      prescasyZbyva: emp.prescasyZbyva,
      pharmacyCode: emp.pharmacyCode ?? "",
      isFlying: !!emp.isFlying,
      allowedPharmacyCodes: Array.isArray(emp.allowedPharmacyCodes)
        ? emp.allowedPharmacyCodes
        : [],
      email: emp.email,
      phone: emp.phone,
    });

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditEmployee(null);
    setError("");
  };

  const refreshEmployees = async () => {
    let url = "";
    if (user.role === "admin" || user.role === "rm") {
      url = `/api/employees/${user.firma}`;
    } else {
      url = `/api/employees/${user.firma}/${user.pharmacyCode}/visible`;
    }

    const res = await fetch(url, { credentials: "include" });
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    setEmployees(list);
  };

  const handleSubmit = async () => {
    if (!canManageEmployees) {
      setError("Nemáte oprávnění upravovat zaměstnance.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const canEditFlying = user.role === "admin" || user.role === "rm";

      let code = form.pharmacyCode;

      if (!form.isFlying && (user.role === "admin" || user.role === "rm")) {
        if (!code) throw new Error("Vyber pobočku.");
        if (user.role === "rm" && !(user.regions || []).includes(code)) {
          throw new Error("RM může zakládat zaměstnance jen ve svých pobočkách.");
        }
      }

      if (form.isFlying) code = null;
      if (user.role === "user" && !form.isFlying) code = user.pharmacyCode || null;

      if (form.isFlying && canEditFlying) {
        if (
          !Array.isArray(form.allowedPharmacyCodes) ||
          form.allowedPharmacyCodes.length === 0
        ) {
          throw new Error(
            "U létajícího zaměstnance vyber alespoň jednu pobočku, kde může létat."
          );
        }

        if (user.role === "rm") {
          const allowed = Array.isArray(form.allowedPharmacyCodes)
            ? form.allowedPharmacyCodes
            : [];
          const regions = Array.isArray(user.regions) ? user.regions : [];
          const bad = allowed.filter((c) => !regions.includes(c));
          if (bad.length) {
            throw new Error(
              `RM může u létače vybírat jen své pobočky. Mimo scope: ${bad.join(", ")}`
            );
          }
        }
      }

      const payload = {
        fullName: form.fullName,
        position: form.position,
        uvazek: Number.parseFloat(String(form.uvazek || "0").replace(",", ".")) || 0,
        denniFond: form.denniFond,
        dovolenaZbyva: form.dovolenaZbyva,
        prescasyZbyva: form.prescasyZbyva,
        pharmacyCode: code,
        isFlying: form.isFlying,
        ...(form.isFlying && canEditFlying
          ? { allowedPharmacyCodes: form.allowedPharmacyCodes }
          : {}),
        email: form.email,
        phone: form.phone,
        firma: user.firma,
      };

      const path = editEmployee ? `/api/employees/${editEmployee._id}` : `/api/employees`;
      const method = editEmployee ? "PUT" : "POST";

      const res = await fetch(path, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Chyba ukládání.");

      await refreshEmployees();
      closeModal();
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Chyba ukládání.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (emp) => {
    if (!canManageEmployees) {
      setError("Nemáte oprávnění mazat zaměstnance.");
      return;
    }

    if (!window.confirm(`Smazat ${emp.fullName}?`)) return;

    try {
      setSaving(true);
      setError("");

      const res = await fetch(`/api/employees/${emp._id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Chyba mazání.");

      setEmployees((p) => p.filter((x) => x._id !== emp._id));
    } catch (err) {
      setError(err.message || "Chyba mazání.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------

  const sortedEmployees = [...employees].sort((a, b) => {
    const codeA = a.pharmacyCode ?? "";
    const codeB = b.pharmacyCode ?? "";
    if (codeA !== codeB) return String(codeA).localeCompare(String(codeB), "cs");

    return a.fullName.localeCompare(b.fullName, "cs", { sensitivity: "base" });
  });

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold mb-2">
        {isUserOnly ? "Zaměstnanci pobočky" : "Zaměstnanci"}
      </h1>
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 p-3 rounded">
          {error}
        </div>
      )}

      {canManageEmployees && (
        <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded shadow"
        >
          + Přidat zaměstnance
        </button>
      )}

      {loading ? (
        <div>Načítám zaměstnance…</div>
      ) : employees.length === 0 ? (
        <div className="text-gray-500">Žádní zaměstnanci nenalezeni.</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded shadow">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2">Příjmení a jméno</th>
                <th className="px-3 py-2">Pozice</th>
                <th className="px-3 py-2">Úvazek</th>
                <th className="px-3 py-2">Dovolená</th>
                <th className="px-3 py-2">NV</th>
                <th className="px-3 py-2">Pobočka</th>
                <th className="px-3 py-2 text-center">Létající</th>
                {!isUserOnly && (
                  <>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Telefon</th>
                  </>
                )}
                {isUserOnly && (
                  <th className="px-3 py-2">Přezdívka pro tisk</th>
                )}
                {canManageEmployees && (
                  <th className="px-3 py-2 text-right">Akce</th>
                )}
              </tr>
            </thead>

            <tbody>
              {sortedEmployees.map((e) => {
                const availableBranches = getAvailableNicknameBranches(e);
                const selectedBranch = getSelectedNicknameBranch(e);
                const nicknameValue =
                  nickDrafts[e._id] ?? getNicknameForBranch(e, selectedBranch);

                return (
                  <tr key={e._id} className="border-t">
                    <td className="px-3 py-2">{e.fullName}</td>
                    <td className="px-3 py-2">{e.position}</td>
                    <td className="px-3 py-2">{Number(e.uvazek).toFixed(2)}</td>
                    <td className="px-3 py-2">{e.dovolenaZbyva}</td>
                    <td className="px-3 py-2">{e.prescasyZbyva}</td>
                    <td className="px-3 py-2">{e.pharmacyCode ?? "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {e.isFlying ? "✔" : ""}
                    </td>
                    {!isUserOnly && (
                      <>
                        <td className="px-3 py-2">{e.email}</td>
                        <td className="px-3 py-2 whitespace-nowrap min-w-[150px]">
                          {e.phone}
                        </td>
                      </>
                    )}

                    {isUserOnly && (
                      <td className="px-3 py-2 min-w-[280px]">
                        {!availableBranches.length ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              className="border p-2 rounded flex-1 min-w-0"
                              value={nicknameValue}
                              onChange={(ev) =>
                                handleNicknameInputChange(e, ev.target.value)
                              }
                              placeholder="Přezdívka"
                            />

                            <button
                              onClick={() => handleSaveNickname(e)}
                              disabled={nickSavingId === e._id}
                              className="px-3 py-2 bg-blue-600 text-white rounded whitespace-nowrap disabled:opacity-60"
                            >
                              {nickSavingId === e._id ? "Ukládám…" : "Uložit"}
                            </button>

                            <button
                              onClick={() => handleDeleteNickname(e)}
                              disabled={nickSavingId === e._id}
                              className="px-3 py-2 bg-gray-200 text-gray-800 rounded whitespace-nowrap disabled:opacity-60"
                            >
                              Smazat
                            </button>
                          </div>
                        )}
                      </td>
                    )}

                    {canManageEmployees && (
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => openEdit(e)}
                            className="text-yellow-600 hover:text-yellow-800 inline-flex items-center gap-1 text-sm"
                          >
                            <Pencil size={16} /> Upravit
                          </button>

                          <button
                            onClick={() => handleDelete(e)}
                            className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-sm"
                          >
                            <Trash2 size={16} /> Smazat
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && canManageEmployees && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded shadow-md max-w-md w-full space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold">
              {editEmployee ? "Upravit zaměstnance" : "Nový zaměstnanec"}
            </h2>

            <div>
              <label className="font-semibold block">Příjmení a jméno</label>
              <input
                className="border p-2 rounded w-full"
                value={form.fullName}
                onChange={(e) => updateForm("fullName", e.target.value)}
              />
            </div>

            <div>
              <label className="font-semibold block">Pozice</label>
              <select
                className="border p-2 rounded w-full"
                value={form.position}
                onChange={(e) => updateForm("position", e.target.value)}
              >
                <option value="farmaceut">Farmaceut</option>
                <option value="asistent">Asistent</option>
                <option value="sanitář">Sanitář</option>
              </select>
            </div>

            <div>
              <label className="font-semibold block">Úvazek</label>
              <input
                type="text"
                inputMode="decimal"
                className="border p-2 rounded w-full"
                value={form.uvazek}
                onChange={(e) => updateForm("uvazek", e.target.value)}
                onBlur={() => {
                  const n = Number.parseFloat(
                    String(form.uvazek || "").replace(",", ".")
                  );
                  if (Number.isFinite(n)) {
                    updateForm("uvazek", n.toFixed(2));
                  } else {
                    updateForm("uvazek", "0.00");
                  }
                }}
                placeholder="1.00"
              />
            </div>

            <div>
              <label className="font-semibold block">Denní fond (hod)</label>
              <input
                disabled
                className="border p-2 rounded w-full bg-gray-100"
                value={Number(form.denniFond).toFixed(2)}
              />
            </div>

            <div>
              <label className="font-semibold block">Zbývá dovolené</label>
              <input
                type="number"
                className="border p-2 rounded w-full"
                value={form.dovolenaZbyva}
                onChange={(e) =>
                  updateForm("dovolenaZbyva", Number(e.target.value))
                }
              />
            </div>

            <div>
              <label className="font-semibold block">Zbývá přesčasů (NV)</label>
              <input
                type="number"
                className="border p-2 rounded w-full"
                value={form.prescasyZbyva}
                onChange={(e) =>
                  updateForm("prescasyZbyva", Number(e.target.value))
                }
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.isFlying}
                onChange={(e) => {
                  const checked = e.target.checked;
                  updateForm("isFlying", checked);

                  if (checked) {
                    updateForm("pharmacyCode", "");
                  } else {
                    updateForm("allowedPharmacyCodes", []);
                  }
                }}
              />
              <label>Létající zaměstnanec</label>
            </div>

            {form.isFlying && (user.role === "admin" || user.role === "rm") && (
              <div className="border rounded p-3 bg-gray-50">
                <div className="font-semibold mb-2">Pobočky, kde může létat</div>

                {(() => {
                  const available =
                    user.role === "admin"
                      ? [...branches].sort((a, b) => Number(a) - Number(b))
                      : [...(user.regions || [])].sort(
                          (a, b) => Number(a) - Number(b)
                        );

                  const toggle = (code) => {
                    const curr = Array.isArray(form.allowedPharmacyCodes)
                      ? form.allowedPharmacyCodes
                      : [];
                    if (curr.includes(code)) {
                      updateForm(
                        "allowedPharmacyCodes",
                        curr.filter((x) => x !== code)
                      );
                    } else {
                      updateForm("allowedPharmacyCodes", [...curr, code]);
                    }
                  };

                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {available.map((b) => (
                        <label key={b} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(form.allowedPharmacyCodes || []).includes(b)}
                            onChange={() => toggle(b)}
                          />
                          <span>{b}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {!form.isFlying && (
              <div>
                <label className="font-semibold block">Pobočka</label>

                {user.role === "admin" ? (
                  <select
                    className="border p-2 rounded w-full"
                    value={form.pharmacyCode}
                    onChange={(e) => updateForm("pharmacyCode", e.target.value)}
                  >
                    <option value="">-- Vyberte pobočku --</option>
                    {[...branches].sort((a, b) => Number(a) - Number(b)).map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                ) : user.role === "rm" ? (
                  <select
                    className="border p-2 rounded w-full"
                    value={form.pharmacyCode}
                    onChange={(e) => updateForm("pharmacyCode", e.target.value)}
                  >
                    <option value="">-- Vyberte pobočku (region) --</option>
                    {[...(user.regions || [])]
                      .sort((a, b) => Number(a) - Number(b))
                      .map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    disabled
                    className="border p-2 rounded w-full bg-gray-100"
                    value={user.pharmacyCode}
                  />
                )}
              </div>
            )}

            <div>
              <label className="font-semibold block">Email</label>
              <input
                className="border p-2 rounded w-full"
                value={form.email}
                onChange={(e) => updateForm("email", e.target.value)}
              />
            </div>

            <div>
              <label className="font-semibold block">Telefon</label>
              <input
                className="border p-2 rounded w-full"
                value={form.phone}
                onChange={(e) => {
                  let v = e.target.value.replace(/\D/g, "");

                  if (!v.startsWith("420") && v.length > 0) {
                    v = "420" + v;
                  }

                  v = v.slice(0, 12);

                  const pretty =
                    "+420 " +
                    v.slice(3, 6) +
                    " " +
                    v.slice(6, 9) +
                    " " +
                    v.slice(9, 12);

                  updateForm("phone", pretty.trim());
                }}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={closeModal}
              >
                Zrušit
              </button>

              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                {saving ? "Ukládám…" : "Uložit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}